import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchOrders, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { useTableState } from "@/lib/useTableState";
import { fmtQty, fmtRelative, statusLabel } from "@/lib/format";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { fillLeg } from "@/components/activity/amountCurrency";
import {
  ActivityScopeBar,
  ResetScopeButton,
  useActivityScope,
  useEventMarketScope,
  validateActivityScopeSearch,
  EMPTY_SCOPE,
  type ActivityScopeSearch,
} from "@/components/activity/ActivityScopeBar";
import {
  ClientIPCell,
  DeviceCell,
  MarketCell,
  ModeCurrencyCell,
  OutcomeCell,
  SideCell,
  StateRow,
  Th,
  UserCell,
} from "@/components/activity/ActivityCells";
import { useScopedPage } from "@/components/activity/useScopedPage";
import { useCurrencySearch } from "@/components/activity/useCurrencySearch";
import { feeRoleTone } from "@/components/fees/feeRole";
import { CurrencyChip } from "@/components/currency/columns";
import {
  NON_STAKE_ORDER_CURRENCY_HINT,
  StakeCurrencyFilter,
  parseStakeCurrency,
  type StakeCurrency,
} from "@/components/currency/StakeCurrencyFilter";

/**
 * The shared Event / Market / User scope, plus this page's own currency
 * filter (SM-511).
 *
 * Same control and same `?currency` key as Active Positions: an order
 * is debited in its channel's debit leg, so the currencies an order can
 * be in are exactly the currencies exposure can be staked in, and a
 * console that used two different pickers for one question would be
 * answering it twice.
 */
export const Route = createFileRoute("/_app/orders/")({
  validateSearch: (
    raw: Record<string, unknown>,
  ): ActivityScopeSearch & { currency?: StakeCurrency } => ({
    ...validateActivityScopeSearch(raw),
    currency: parseStakeCurrency(raw.currency),
  }),
  component: ActiveOrdersPage,
});

interface OrderFilters {
  status: string;
  side: string;
}

// This page reads order-service's ACTIVE `orders` table: a row is
// archived to order_history and deleted the moment it turns terminal
// (FILLED / CANCELED / REJECTED / EXPIRED / FAILED). So the only
// statuses that can ever reach the table — and the only ones worth
// filtering on — are the active set. Offering "Filled" or "Canceled"
// here produced a permanently empty result set, and the value "PARTIAL"
// never matched anything either (the wire status is PARTIALLY_FILLED).
// Terminal orders live on the Order History page.
const ACTIVE_ORDER_STATUSES = [
  { value: "OPEN", label: "Open" },
  { value: "PARTIALLY_FILLED", label: "Partially filled" },
  { value: "NEW", label: "New" },
  { value: "RESERVED", label: "Reserved" },
  { value: "PENDING_ENGINE", label: "Pending engine" },
  { value: "CANCEL_PENDING", label: "Cancel pending" },
];
const ACTIVE_ORDER_STATUS_VALUES = new Set(
  ACTIVE_ORDER_STATUSES.map((s) => s.value),
);

// Resting vs working: OPEN / PARTIALLY_FILLED are live on the book,
// everything else in the active set is a short-lived in-flight state
// (a row that lingers there is worth an operator's attention).
function statusTone(status: string): "success" | "warning" | "muted" {
  if (status === "OPEN") return "success";
  if (status === "PARTIALLY_FILLED") return "warning";
  return "muted";
}

function ActiveOrdersPage() {
  const table = useTableState<OrderFilters>({
    defaultSort: { by: "created_at", dir: "desc" },
    defaultFilters: { status: "", side: "" },
  });
  const { scope, setScope, resetKey, hasScope } = useActivityScope();
  const [currency, setCurrency] = useCurrencySearch(parseStakeCurrency);
  const marketScope = useEventMarketScope(scope);
  // The currency joins the scope's reset key: it narrows the result set
  // the same way, so a page-5 operator who picks a currency lands back
  // on page 1 rather than past the end of a shorter list — and never
  // looks at the previous currency's rows while the new page loads.
  useScopedPage(`${resetKey}|${currency ?? ""}`, table.setPage);

  // Never forward a non-active status upstream — an out-of-set value
  // would replace the server's active-set predicate and turn the page
  // into a terminal-order view. Falling back to "" keeps the default
  // active-only result set (rows, total and pagination all agree).
  const status = ACTIVE_ORDER_STATUS_VALUES.has(table.filters.status)
    ? table.filters.status
    : undefined;
  const params = {
    status,
    side: table.filters.side || undefined,
    market_id: marketScope.marketIDs,
    user_id: scope.userId || undefined,
    currency,
  };
  const orders = useQuery({
    queryKey: keys.ordersList(table.limit, table.offset, table.sort, params),
    queryFn: () =>
      fetchOrders({
        limit: table.limit,
        offset: table.offset,
        sort: table.sort,
        ...params,
      }),
    // Hold the read until an event filter has resolved to its markets,
    // or the page would flash every market's orders under an event chip.
    enabled: !marketScope.pending,
  });

  const filtered = hasScope || table.hasActiveFilters || Boolean(currency);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Active orders</h1>
        <p className="text-sm text-muted">
          Live cross-user book — open, partially filled, and in-flight orders only.
          Filled and canceled orders are on Order History.
        </p>
      </div>
      <ActivityScopeBar scope={scope} onChange={setScope}>
        <div className="w-44">
          <Select
            label="Status"
            placeholder="Any active"
            value={table.filters.status}
            onChange={(e) => table.setFilter("status", e.target.value)}
            options={ACTIVE_ORDER_STATUSES}
          />
        </div>
        <div className="w-52">
          <StakeCurrencyFilter value={currency} onChange={setCurrency} />
        </div>
        <div className="w-32">
          <Select
            label="Side"
            placeholder="Any"
            value={table.filters.side}
            onChange={(e) => table.setFilter("side", e.target.value)}
            options={[
              { value: "BUY", label: "Buy" },
              { value: "SELL", label: "Sell" },
            ]}
          />
        </div>
        {/* basis-full: its own line under the bar, so the two-line note
            does not drag the controls beside it out of alignment. */}
        <p className="basis-full text-2xs text-t-4">{NON_STAKE_ORDER_CURRENCY_HINT}</p>
        <ResetScopeButton
          show={filtered}
          onReset={() => {
            table.reset();
            setCurrency(undefined);
            setScope(EMPTY_SCOPE);
          }}
        />
      </ActivityScopeBar>
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Active orders</CardTitle>
          <span className="text-2xs text-muted">{orders.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <Th className="px-5">Market</Th>
                  {/* SM-469: which book and which currency. Never merged
                      into the market column — they are separate facts. */}
                  <Th>Mode · currency</Th>
                  <Th>User</Th>
                  <Th>Outcome</Th>
                  <Th>Side</Th>
                  <SortableHeader label="Price" field="price" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <SortableHeader label="Qty" field="quantity" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <SortableHeader label="Filled" field="filled_quantity" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <Th>Fee</Th>
                  <Th>Status</Th>
                  {/* SM-821: where the order was placed from — "—" when
                      the client log recorded nothing. */}
                  <Th>IP</Th>
                  <Th>Device</Th>
                  <SortableHeader label="Created" field="created_at" sort={table.sort} onSortChange={table.setSort} align="right" className="px-5" />
                </tr>
              </thead>
              <tbody>
                {(orders.data?.items ?? []).map((o) => (
                  <tr key={o.order_id} className="border-b border-border/60 last:border-0">
                    <MarketCell row={o} />
                    <ModeCurrencyCell row={o} />
                    <UserCell row={o} />
                    <OutcomeCell row={o} />
                    {/* Intent space — a "Buy NO" reads BUY here, the way
                        the trader placed it, not the book's SELL leg. */}
                    <SideCell side={o.intent_side || o.side} />
                    <td className="px-3 py-2.5 text-right font-mono">
                      {/* SM-549: the leg this order's money moved on, not
                          a hardcoded GOLD. It can differ from the chip's
                          debit leg — see amountCurrency.tsx. */}
                      <CurrencyAmount
                        currency={fillLeg(o, o.side)}
                        value={o.intent_price || o.price}
                        trimZeros
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {fmtQty(o.quantity, o.amount_precision)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {fmtQty(o.filled_quantity, o.amount_precision)}
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      {o.fee_role ? (
                        <div className="flex items-center gap-1.5">
                          {/* Three roles now (SM-327), so this cannot be a
                              binary: an unmatched value used to fall
                              through to taker styling, which would have
                              made a redemption fee look like a trading
                              one. */}
                          <Badge tone={feeRoleTone(o.fee_role)}>
                            {o.fee_role}
                          </Badge>
                          <span className="font-mono text-2xs text-t-2">
                            <CurrencyAmount
                              currency={fillLeg(o, o.side)}
                              value={o.fee_total_amount ?? "0"}
                            />
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={statusTone(o.status)}>{statusLabel(o.status)}</Badge>
                    </td>
                    <ClientIPCell ip={o.client_ip} />
                    <DeviceCell ua={o.user_agent} />
                    <td className="px-5 py-2.5 text-right text-2xs text-muted">
                      {fmtRelative(o.created_at)}
                    </td>
                  </tr>
                ))}
                {(orders.isLoading || marketScope.pending) && (
                  <StateRow colSpan={13}>Loading…</StateRow>
                )}
                {orders.isError && (
                  <StateRow colSpan={13}>Could not load orders. Retry in a moment.</StateRow>
                )}
                {orders.data && orders.data.items.length === 0 && (
                  <StateRow colSpan={13}>
                    {/* Name the currency when one is picked: "no orders
                        for these filters" beside a Currency control
                        reads as a broken filter, while "no active
                        orders in RED" is an answer. */}
                    {currency ? (
                      <>
                        No active orders in <CurrencyChip currency={currency} />
                        {hasScope || table.hasActiveFilters ? " for these filters" : ""}.
                      </>
                    ) : (
                      <>No active orders{filtered ? " for these filters" : ""}.</>
                    )}
                  </StateRow>
                )}
              </tbody>
            </table>
          </div>
          {orders.data && (
            <Pagination
              page={orders.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="orders"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
